/**
 * Convert common video share URLs into iframe-safe embed URLs.
 * YouTube watch/share pages refuse to load in iframes; embed URLs work.
 */
export function toVideoEmbedUrl(url: string): string {
    const trimmed = url.trim();
    if (!trimmed) return trimmed;

    try {
        const parsed = new URL(trimmed);
        const host = parsed.hostname.replace(/^www\./, "");

        if (host === "youtu.be") {
            const id = parsed.pathname.split("/").filter(Boolean)[0];
            if (id) return `https://www.youtube.com/embed/${id}`;
        }

        if (host === "youtube.com" || host === "m.youtube.com" || host === "youtube-nocookie.com") {
            if (parsed.pathname.startsWith("/embed/")) {
                return `https://www.youtube.com${parsed.pathname}`;
            }

            const shortsMatch = parsed.pathname.match(/^\/shorts\/([^/]+)/);
            if (shortsMatch?.[1]) {
                return `https://www.youtube.com/embed/${shortsMatch[1]}`;
            }

            const id = parsed.searchParams.get("v");
            if (id) return `https://www.youtube.com/embed/${id}`;
        }

        if (host === "vimeo.com") {
            const id = parsed.pathname.split("/").filter(Boolean)[0];
            if (id && /^\d+$/.test(id)) {
                return `https://player.vimeo.com/video/${id}`;
            }
        }

        if (host === "player.vimeo.com" && parsed.pathname.startsWith("/video/")) {
            return trimmed;
        }
    } catch {
        return trimmed;
    }

    return trimmed;
}

/**
 * Convert embed/share URLs into a normal watch URL for "Open in new tab".
 * Never returns a YouTube /embed/ URL.
 */
export function toVideoExternalUrl(url: string): string {
    const trimmed = url.trim();
    if (!trimmed) return trimmed;

    try {
        const parsed = new URL(trimmed);
        const host = parsed.hostname.replace(/^www\./, "");

        if (host === "youtu.be") {
            const id = parsed.pathname.split("/").filter(Boolean)[0];
            if (id) return `https://youtu.be/${id}`;
        }

        if (host === "youtube.com" || host === "m.youtube.com" || host === "youtube-nocookie.com") {
            const embedMatch = parsed.pathname.match(/^\/embed\/([^/]+)/);
            if (embedMatch?.[1]) {
                return `https://youtu.be/${embedMatch[1]}`;
            }

            const shortsMatch = parsed.pathname.match(/^\/shorts\/([^/]+)/);
            if (shortsMatch?.[1]) {
                return `https://youtu.be/${shortsMatch[1]}`;
            }

            const id = parsed.searchParams.get("v");
            if (id) return `https://youtu.be/${id}`;
        }

        if (host === "vimeo.com") {
            return trimmed;
        }

        if (host === "player.vimeo.com") {
            const id = parsed.pathname.split("/").filter(Boolean)[1];
            if (id && /^\d+$/.test(id)) {
                return `https://vimeo.com/${id}`;
            }
        }
    } catch {
        return trimmed;
    }

    return trimmed;
}
